/**
 * AuditTrailPanel — Workspace Settings panel for unified audit trail
 *
 * sl-wmbt.5.1: Setup, feature gate, GraphQL query integration, upgrade prompt.
 * sl-wmbt.5.2: Event list, filters, pagination, skeleton loader.
 * sl-97oo.4.1: CSV export.
 *
 * Plan-gated: Only visible for Team+ workspaces.
 * Role-gated: Only Admin/Owner can see this tab.
 *
 * For non-Team workspaces (if tab becomes visible somehow), an upgrade
 * prompt is shown instead of the event list.
 */
import { Button, notify } from '@affine/component';
import { FeatureUpgradeNudge } from '@affine/core/components/affine/feature-gate';
import { useBackendAvailable } from '@affine/core/components/hooks/use-backend-availability';
import { useFeatureGate } from '@affine/core/modules/streamlain/hooks/use-feature-gate';
import { WorkspaceService } from '@affine/core/modules/workspace';
import {
  isBackendUnavailableError,
  markBackendUnavailable,
} from '@affine/core/utils/backend-availability';
import { useI18n } from '@affine/i18n';
import { DownloadIcon } from '@blocksuite/icons/rc';
import { useLiveData, useService } from '@toeverything/infra';
import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  SettingsOfflineHeaderAction,
  useSettingsOfflineIncompleteLabel,
} from '../../shared/settings-offline-status';
import {
  SettingsEntityPage,
  SettingsSearchableEntityPage,
} from '../shared/settings-entity-page';
import { requestWorkspaceSettingGraphQL } from '../shared/workspace-settings-api';
import {
  AuditFilters,
  type WorkspaceGroupOption,
  type WorkspaceMemberOption,
} from './audit-filters';
import { resolveAuditPrincipalDisplay } from './audit-principal';
import { EventList } from './event-list';
import { EventSkeleton } from './event-skeleton';
import type { AuditEvent, AuditEventPage, AuditFilter } from './types';

const PAGE_SIZE = 25;

const WORKSPACE_AUDIT_EVENTS_QUERY = /* GraphQL */ `
  query WorkspaceAuditEvents(
    $workspaceId: String!
    $filter: AuditEventFilterInput
    $first: Int
    $after: String
  ) {
    workspaceAuditEvents(
      workspaceId: $workspaceId
      filter: $filter
      first: $first
      after: $after
    ) {
      edges {
        node {
          id
          workspaceId
          userId
          userEmail
          userName
          userAvatarUrl
          category
          action
          targetType
          targetId
          targetName
          details
          ipAddress
          createdAt
        }
        cursor
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`;

const WORKSPACE_MEMBERS_QUERY = /* GraphQL */ `
  query WorkspaceMembersForAudit($workspaceId: String!) {
    workspace(id: $workspaceId) {
      members(take: 200) {
        id
        name
        email
        avatarUrl
      }
    }
  }
`;

const WORKSPACE_GROUPS_QUERY = /* GraphQL */ `
  query WorkspaceGroupsForAudit($workspaceId: String!) {
    groups(workspaceId: $workspaceId) {
      id
      name
      description
    }
  }
`;

interface GraphqlResponse<T> {
  data?: T;
  errors?: { message: string }[];
}

async function postGraphQL<T>(
  workspaceId: string,
  query: string,
  variables: Record<string, unknown>
): Promise<T> {
  const res = await requestWorkspaceSettingGraphQL(
    workspaceId,
    query,
    variables
  );
  const json = (await res.json()) as GraphqlResponse<T>;
  if (json.errors?.length) {
    throw new Error(json.errors[0]?.message ?? 'GraphQL error');
  }
  if (!json.data) {
    throw new Error('Empty GraphQL response');
  }
  return json.data;
}

// ── CSV Export ──────────────────────────────────────────────────────────

function escapeCsvField(value: string): string {
  // CSV injection protection: prefix formula-triggering chars with a tab
  const needsFormulaGuard = /^[=+\-@\t\r]/.test(value);
  const needsQuoting =
    value.includes(',') ||
    value.includes('"') ||
    value.includes('\n') ||
    value.includes('\r');

  let escaped = value;
  if (needsFormulaGuard) {
    escaped = '\t' + escaped;
  }
  if (needsQuoting || needsFormulaGuard) {
    return `"${escaped.replace(/"/g, '""')}"`;
  }
  return escaped;
}

interface CsvMetaContext {
  workspaceName: string;
  filter: AuditFilter;
  members: WorkspaceMemberOption[];
  groups: WorkspaceGroupOption[];
}

function formatMetaDate(iso: string | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  const hours = String(d.getHours()).padStart(2, '0');
  const minutes = String(d.getMinutes()).padStart(2, '0');
  return `${day}.${month}.${year} ${hours}:${minutes}`;
}

function resolvePrincipalLabel(
  filter: AuditFilter,
  members: WorkspaceMemberOption[],
  groups: WorkspaceGroupOption[],
  t: ReturnType<typeof useI18n>
): string {
  if (filter.userId) {
    const m = members.find(x => x.id === filter.userId);
    return m?.name || m?.email || filter.userId;
  }
  if (filter.groupId) {
    const g = groups.find(x => x.id === filter.groupId);
    const prefix =
      t['com.streamlain.settings.workspace.audit-trail.filter.group-prefix']();
    return g ? `${prefix} ${g.name}` : `${prefix} ${filter.groupId}`;
  }
  return t[
    'com.streamlain.settings.workspace.audit-trail.filter.all-principals'
  ]();
}

function buildCsv(
  events: AuditEvent[],
  meta: CsvMetaContext,
  t: ReturnType<typeof useI18n>
): string {
  // sl-97oo: "Hübsch formatierte" Metadaten im Kopf der Datei. Die Meta-Zeilen
  // stehen in Spalte A — Excel akzeptiert variable Zeilenlängen, und ein BOM
  // wird von downloadCsv() vorne angehängt.
  const now = new Date().toISOString();
  const principalLabel = resolvePrincipalLabel(
    meta.filter,
    meta.members,
    meta.groups,
    t
  );

  // Fallback auf erstes/letztes createdAt wenn Datumsfilter nicht gesetzt.
  // Die Events kommen aus dem Repository bereits createdAt DESC sortiert,
  // deshalb ist events[0] das jüngste und events[last] das älteste.
  const sortedAsc = [...events].sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
  );
  const effectiveFrom =
    meta.filter.dateFrom ??
    (sortedAsc.length > 0 ? sortedAsc[0].createdAt : undefined);
  const effectiveTo =
    meta.filter.dateTo ??
    (sortedAsc.length > 0
      ? sortedAsc[sortedAsc.length - 1].createdAt
      : undefined);

  const metaLines: string[][] = [
    [t['com.streamlain.settings.workspace.audit-trail.title']()],
    [],
    [
      t[
        'com.streamlain.settings.workspace.audit-trail.export.meta.workspace'
      ](),
      meta.workspaceName,
    ],
    [
      t[
        'com.streamlain.settings.workspace.audit-trail.export.meta.exported-at'
      ](),
      formatMetaDate(now),
    ],
    [
      t[
        'com.streamlain.settings.workspace.audit-trail.export.meta.filter-principal'
      ](),
      principalLabel,
    ],
    [
      t[
        'com.streamlain.settings.workspace.audit-trail.export.meta.date-from'
      ](),
      formatMetaDate(effectiveFrom),
    ],
    [
      t['com.streamlain.settings.workspace.audit-trail.export.meta.date-to'](),
      formatMetaDate(effectiveTo),
    ],
    [
      t[
        'com.streamlain.settings.workspace.audit-trail.export.meta.event-count'
      ](),
      String(events.length),
    ],
    [],
  ];

  const header = [
    t['com.streamlain.settings.workspace.audit-trail.column.time'](),
    t['com.streamlain.settings.workspace.audit-trail.column.category'](),
    t['com.streamlain.settings.workspace.audit-trail.column.action'](),
    t['com.streamlain.settings.workspace.audit-trail.column.user'](),
    t['com.streamlain.settings.workspace.audit-trail.detail.target-type'](),
    t['com.streamlain.settings.workspace.audit-trail.column.target'](),
    t['com.streamlain.settings.workspace.audit-trail.detail.ip'](),
    t['com.streamlain.settings.workspace.audit-trail.detail.details'](),
  ];

  const rows = events.map(event => [
    new Date(event.createdAt).toISOString(),
    event.category,
    event.action,
    resolveAuditPrincipalDisplay(
      event,
      meta.members,
      t['com.streamlain.settings.workspace.audit-trail.user.system'](),
      t['com.streamlain.settings.workspace.audit-trail.user.unknown']()
    ).label,
    event.targetType ?? '',
    event.targetName ?? '',
    event.ipAddress ?? '',
    event.details ? JSON.stringify(event.details) : '',
  ]);

  const lines: string[] = [];
  for (const line of metaLines) {
    lines.push(line.map(escapeCsvField).join(','));
  }
  lines.push(header.map(escapeCsvField).join(','));
  for (const row of rows) {
    lines.push(row.map(escapeCsvField).join(','));
  }
  return lines.join('\n');
}

/** Fetch ALL events for the current filter (paginated, max 100 per page) */
async function fetchAllEvents(
  workspaceId: string,
  filter: Record<string, unknown> | undefined
): Promise<AuditEvent[]> {
  const allEvents: AuditEvent[] = [];
  let cursor: string | undefined;
  let hasMore = true;

  while (hasMore) {
    const data = await postGraphQL<{
      workspaceAuditEvents: AuditEventPage;
    }>(workspaceId, WORKSPACE_AUDIT_EVENTS_QUERY, {
      workspaceId,
      filter,
      first: 100,
      after: cursor,
    });
    const page = data.workspaceAuditEvents;
    allEvents.push(...page.edges.map(e => e.node));
    hasMore = page.pageInfo.hasNextPage;
    cursor = page.pageInfo.endCursor ?? undefined;
  }

  return allEvents;
}

function downloadCsv(csv: string, filename: string): void {
  // UTF-8 BOM for correct Excel encoding
  const bom = '﻿';
  const blob = new Blob([bom + csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ── Error state ─────────────────────────────────────────────────────────

interface AuditErrorStateProps {
  message: string;
  detail: string;
  retryLabel: string;
  onRetry: () => void;
}

const AuditErrorState = ({
  message,
  detail,
  retryLabel,
  onRetry,
}: AuditErrorStateProps) => (
  <div
    style={{
      textAlign: 'center',
      padding: '24px 0',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '8px',
    }}
  >
    <p>{message}</p>
    <code style={{ fontSize: '12px', fontFamily: 'monospace' }}>{detail}</code>
    <Button variant="secondary" onClick={onRetry}>
      {retryLabel}
    </Button>
  </div>
);

// ── Main Panel ──────────────────────────────────────────────────────────

export const AuditTrailPanel = () => {
  const t = useI18n();
  const backendAvailable = useBackendAvailable();
  const offlineLabel = useSettingsOfflineIncompleteLabel();
  const workspaceService = useService(WorkspaceService);
  const workspaceId = workspaceService.workspace.id;
  const workspaceName =
    useLiveData(workspaceService.workspace.name$) ?? workspaceId;
  const { isFeatureAllowed } = useFeatureGate();
  const hasAuditTrail = isFeatureAllowed('audit-trail');

  const [events, setEvents] = useState<AuditEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [endCursor, setEndCursor] = useState<string | null>(null);
  const [filter, setFilter] = useState<AuditFilter>({});
  const [searchQuery, setSearchQuery] = useState('');
  const [members, setMembers] = useState<WorkspaceMemberOption[]>([]);
  const [groups, setGroups] = useState<WorkspaceGroupOption[]>([]);

  // Build GraphQL filter object — skip undefined values
  const graphqlFilter = useMemo(() => {
    const f: Record<string, unknown> = {};
    const search = searchQuery.trim();
    if (filter.category) f.category = filter.category;
    if (filter.userId) f.userId = filter.userId;
    if (filter.groupId) f.groupId = filter.groupId;
    if (filter.dateFrom) f.dateFrom = new Date(filter.dateFrom).toISOString();
    if (filter.dateTo) f.dateTo = new Date(filter.dateTo).toISOString();
    if (search) f.search = search;
    return Object.keys(f).length > 0 ? f : undefined;
  }, [filter, searchQuery]);

  const fetchEvents = useCallback(
    async (cursor?: string | null, append = false) => {
      if (!hasAuditTrail) return;
      if (!backendAvailable) {
        setLoading(false);
        setLoadingMore(false);
        return;
      }
      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }
      setError(null);
      try {
        const data = await postGraphQL<{
          workspaceAuditEvents: AuditEventPage;
        }>(workspaceId, WORKSPACE_AUDIT_EVENTS_QUERY, {
          workspaceId,
          filter: graphqlFilter,
          first: PAGE_SIZE,
          after: cursor ?? undefined,
        });
        const page = data.workspaceAuditEvents;
        const newEvents = page.edges.map(e => e.node);
        setEvents(prev => (append ? [...prev, ...newEvents] : newEvents));
        setHasNextPage(page.pageInfo.hasNextPage);
        setEndCursor(page.pageInfo.endCursor);
      } catch (err) {
        if (isBackendUnavailableError(err)) {
          markBackendUnavailable(err);
        }
        setError(err instanceof Error ? err.message : 'Unknown error');
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [backendAvailable, workspaceId, graphqlFilter, hasAuditTrail]
  );

  // Fetch workspace members + groups for the principal filter (sl-97oo)
  useEffect(() => {
    if (!hasAuditTrail || !backendAvailable) return;
    postGraphQL<{
      workspace: { members: WorkspaceMemberOption[] };
    }>(workspaceId, WORKSPACE_MEMBERS_QUERY, { workspaceId })
      .then(data => setMembers(data.workspace.members ?? []))
      .catch(() => {
        // Non-fatal — filter just shows empty user section
      });
    postGraphQL<{ groups: WorkspaceGroupOption[] }>(
      workspaceId,
      WORKSPACE_GROUPS_QUERY,
      {
        workspaceId,
      }
    )
      .then(data => setGroups(data.groups ?? []))
      .catch(() => {
        // Non-fatal — filter just shows empty group section
      });
  }, [backendAvailable, workspaceId, hasAuditTrail]);

  useEffect(() => {
    void fetchEvents();
  }, [fetchEvents]);

  const loadMore = useCallback(() => {
    if (endCursor) {
      void fetchEvents(endCursor, true);
    }
  }, [endCursor, fetchEvents]);

  const [exporting, setExporting] = useState(false);

  const handleExport = useCallback(async () => {
    if (exporting) return;
    setExporting(true);
    try {
      if (!backendAvailable) return;
      const allEvents = await fetchAllEvents(workspaceId, graphqlFilter);
      if (allEvents.length === 0) return;
      const csv = buildCsv(
        allEvents,
        { workspaceName, filter, members, groups },
        t
      );
      const date = new Date().toISOString().slice(0, 10);
      downloadCsv(csv, `audit-trail-${date}.csv`);
      notify.success({
        title:
          t['com.streamlain.settings.workspace.audit-trail.export-success'](),
      });
    } catch (err) {
      if (isBackendUnavailableError(err)) {
        markBackendUnavailable(err);
      }
      notify.error({
        title:
          t['com.streamlain.settings.workspace.audit-trail.export-error'](),
      });
    } finally {
      setExporting(false);
    }
  }, [
    exporting,
    backendAvailable,
    workspaceId,
    graphqlFilter,
    workspaceName,
    filter,
    members,
    groups,
    t,
  ]);

  const title = t['com.streamlain.settings.workspace.audit-trail.title']();
  const subtitle =
    t['com.streamlain.settings.workspace.audit-trail.description']();

  // Upgrade prompt for non-Team workspaces (defensive — tab should already be hidden)
  if (!hasAuditTrail) {
    return (
      <SettingsEntityPage title={title} subtitle={subtitle}>
        <FeatureUpgradeNudge
          featureKey="audit-trail"
          featureName={t[
            'com.streamlain.settings.workspace.audit-trail.upgrade-title'
          ]()}
          requiredPlan="Team"
        />
      </SettingsEntityPage>
    );
  }

  const exportAction = (
    <Button
      variant="secondary"
      prefix={<DownloadIcon />}
      onClick={() => void handleExport()}
      loading={exporting}
      disabled={events.length === 0 || exporting}
      data-testid="audit-export-csv"
    >
      {t['com.streamlain.settings.workspace.audit-trail.export']()}
    </Button>
  );

  const filtersBlock = (
    <AuditFilters
      filter={filter}
      onFilterChange={setFilter}
      workspaceMembers={members}
      workspaceGroups={groups}
    />
  );

  const emptyContent = error ? (
    <AuditErrorState
      message={t['com.streamlain.settings.workspace.audit-trail.error']()}
      detail={error}
      retryLabel={t['com.streamlain.settings.workspace.audit-trail.retry']()}
      onRetry={() => void fetchEvents()}
    />
  ) : (
    <p data-testid="audit-empty-state">
      {t['com.streamlain.settings.workspace.audit-trail.empty']()}
    </p>
  );

  return (
    <SettingsSearchableEntityPage
      title={title}
      subtitle={subtitle}
      headerAction={<SettingsOfflineHeaderAction show={!backendAvailable} />}
      searchValue={searchQuery}
      onSearchChange={setSearchQuery}
      searchPlaceholder={t[
        'com.streamlain.settings.workspace.audit-trail.search-placeholder'
      ]()}
      action={exportAction}
      banner={filtersBlock}
      loading={loading && backendAvailable}
      hasItems={!error && events.length > 0}
      loadingContent={<EventSkeleton />}
      emptyContent={
        !backendAvailable ? (
          <p data-testid="audit-offline-state">{offlineLabel}</p>
        ) : (
          emptyContent
        )
      }
    >
      <EventList events={events} members={members} />
      {loadingMore && (
        <p>{t['com.streamlain.settings.workspace.audit-trail.loading']()}</p>
      )}
      {hasNextPage && !loadingMore && (
        <Button variant="secondary" onClick={loadMore}>
          {t['com.streamlain.settings.workspace.audit-trail.load-more']()}
        </Button>
      )}
    </SettingsSearchableEntityPage>
  );
};
